import { map, firstValueFrom } from "rxjs";
import { AssessmentExportTemplateInstance, AssessmentExportTemplateSection, AssessmentSectionListItem, DataFlowDataExport, DataFlowEdgeExport, DataFlowNodeExport } from "../models/assessment-export.interface";
import { ContentPipe } from "src/app/services/content/content.pipe";
import { CustomValuesQuery } from "src/app/company-settings/state/custom-values/custom-values.query";
import { EmployeeDataMapping } from "src/app/api/models/data-mapping/data-mapping.interface";
import { Injectable } from "@angular/core";
import { SpecialSystemType } from "src/app/systems/models/custom-system-type.enum";
import { SystemsQuery } from "src/app/systems/state/systems.query";
import { EmployeesService } from "src/app/employees/state/employees.service";
import { AssessmentStatusPipe } from "../pipes/assessment-status.pipe";
import { CustomValueTypeEnum } from "src/app/api/models/company-settings/custom-values.enum";
import { LoggerService } from "src/app/logger/logger.service";
import { Risk } from "src/app/risks/models/risks.interface";
import { AiAssessmentLabelsPipe } from "../pipes/ai-assessment-labels.pipe";
import { RiskCalulationToTextPipe } from "../pipes/risk-calculation-to-text.pipe";
import { AssessmentAttachment, AssessmentCustomFieldDropdown, AssessmentCustomFieldText, AssessmentDataFlow, AssessmentDataFlowNode, AssessmentDataTypeSource, AssessmentDropdownOption, AssessmentEntity, AssessmentResourceId, PartiesWithAccess } from "src/app/api/models/assessments/assessment.interface";
import { AssessmentQuestionTypeEnum, AssessmentTemplateEnum, DataFlowNodeTypeEnum } from "src/app/api/models/assessments/assessment.enum";
import { AssessmentPage, AssessmentQuestion, AssessmentSection } from "../models/assessments.interface";
import { AssessmentExportedQuestionTypeEnum } from "../models/assessment-export.enum";
import { CustomFieldsV2Service } from "src/app/company-settings/custom-fields-v2/custom-fields-v2.service";
import { AssessmentsMapperService } from "./assessments-mapper.service";
import { extractEvidenceFileHrefs, replaceEvidenceFileHrefs } from "../utils/evidence-reasoning-html.util";
import { SystemFilesService } from "src/app/systems/services/system-files.service";

@Injectable({
  providedIn: 'root'
})
export class AiAssessmentsExportHelper {

  private readonly loggerName = 'AiAssessmentsExportHelper';

  private exportQuestionTypeMap: Map<AssessmentQuestionTypeEnum, AssessmentExportedQuestionTypeEnum> = new Map([
    [AssessmentQuestionTypeEnum.BusinessOwner, AssessmentExportedQuestionTypeEnum.BusinessOwner],
    [AssessmentQuestionTypeEnum.BusinessPurpose, AssessmentExportedQuestionTypeEnum.BusinessPurpose],
    [AssessmentQuestionTypeEnum.BusinessUnit, AssessmentExportedQuestionTypeEnum.BusinessUnit],
    [AssessmentQuestionTypeEnum.DataSources, AssessmentExportedQuestionTypeEnum.DataSources],
    [AssessmentQuestionTypeEnum.DataSubjects, AssessmentExportedQuestionTypeEnum.DataSubjects],
    [AssessmentQuestionTypeEnum.DataRole, AssessmentExportedQuestionTypeEnum.DataRole],
    [AssessmentQuestionTypeEnum.DataTypes, AssessmentExportedQuestionTypeEnum.DataTypes],
    [AssessmentQuestionTypeEnum.LegalBasis, AssessmentExportedQuestionTypeEnum.LegalBasis],
    [AssessmentQuestionTypeEnum.TransferMechanism, AssessmentExportedQuestionTypeEnum.TransferMechanism],
    [AssessmentQuestionTypeEnum.PartiesWithAccess, AssessmentExportedQuestionTypeEnum.PartiesWithAccess],
    [AssessmentQuestionTypeEnum.DpiaNeeded, AssessmentExportedQuestionTypeEnum.DpiaNeeded],
    [AssessmentQuestionTypeEnum.DropdownCustomField, AssessmentExportedQuestionTypeEnum.MultipleChoice],
    [AssessmentQuestionTypeEnum.TextCustomField, AssessmentExportedQuestionTypeEnum.Text],
    [AssessmentQuestionTypeEnum.DataFlow, AssessmentExportedQuestionTypeEnum.DataFlow],
    [AssessmentQuestionTypeEnum.Datepicker, AssessmentExportedQuestionTypeEnum.Datepicker],
    [AssessmentQuestionTypeEnum.File, AssessmentExportedQuestionTypeEnum.File],
    [AssessmentQuestionTypeEnum.LinkedAssessment, AssessmentExportedQuestionTypeEnum.LinkedAssessment],
    [AssessmentQuestionTypeEnum.Risks, AssessmentExportedQuestionTypeEnum.Risks],
    [AssessmentQuestionTypeEnum.ShortAnswer, AssessmentExportedQuestionTypeEnum.Text],
    [AssessmentQuestionTypeEnum.Text, AssessmentExportedQuestionTypeEnum.Text],
    [AssessmentQuestionTypeEnum.TextBlock, AssessmentExportedQuestionTypeEnum.TextBlock],
    [AssessmentQuestionTypeEnum.Textarea, AssessmentExportedQuestionTypeEnum.Textarea],
    [AssessmentQuestionTypeEnum.Dropdown, AssessmentExportedQuestionTypeEnum.MultipleChoice],
    [AssessmentQuestionTypeEnum.Group, AssessmentExportedQuestionTypeEnum.Group],
    [AssessmentQuestionTypeEnum.NewSection, AssessmentExportedQuestionTypeEnum.NewSection],
    [AssessmentQuestionTypeEnum.CustomComponent, AssessmentExportedQuestionTypeEnum.CustomComponent],
  ]);

  constructor(
    private systemsQuery: SystemsQuery,
    private customValuesQuery: CustomValuesQuery,
    private customFieldsV2Service: CustomFieldsV2Service,
    private employeesService: EmployeesService,
    private contentPipe: ContentPipe,
    private loggerService: LoggerService,
    private aiAssessmentStatusPipe: AssessmentStatusPipe,
    private aiAssessmentLabelsPipe: AiAssessmentLabelsPipe,
    private riskCalculationTextPipe: RiskCalulationToTextPipe,
    private assessmentsMapperService: AssessmentsMapperService,
    private systemFilesService: SystemFilesService,
  ) { }

  /**
   * Resolves the uploaded-document citations in a reasoning HTML string to openable links: each
   * relative `/settings/ai/files/…/download-url` path is exchanged (authenticated) for its short-lived
   * signed download URL. Unresolved paths are left as-is so the export renders them as plain text
   * rather than a broken/401 link. Signed URLs expire, so the links work for a limited time after export.
   */
  private async resolveEvidenceReasoning(html: string | null | undefined): Promise<string | null> {
    if (!html) {
      return null;
    }
    const hrefs = extractEvidenceFileHrefs(html);
    if (!hrefs.length) {
      return html;
    }
    const resolved = new Map<string, string>();
    await Promise.all(hrefs.map(async href => {
      try {
        const { downloadUrl } = await firstValueFrom(this.systemFilesService.getFileDownloadUrl(href));
        if (downloadUrl) {
          resolved.set(href, downloadUrl);
        }
      }
      catch (error) {
        this.loggerService.error(this.loggerName, `Failed to resolve evidence file link "${href}": ${(error as Error)?.message ?? error}`);
      }
    }));
    return replaceEvidenceFileHrefs(html, resolved);
  }

  // Server-rendered PDF graph draws the location pin unconditionally and labels it from serverLocations[0].
  // Mirror the in-app data-flow node (ropa.noLocationSet) so empty nodes show "No location set" instead of a bare pin.
  private resolveNodeLocations(serverLocations: string[] | undefined): string[] {
    return serverLocations?.length ? serverLocations : [this.contentPipe.transform('ropa.noLocationSet')];
  }

  private buildFlowNode(
    name: string,
    type: DataFlowNodeTypeEnum,
    meta: { serverLocations: string[]; iconPath?: string },
  ): DataFlowNodeExport {
    return {
      id: name,
      label: name,
      dimension: { width: 80, height: 100 },
      meta: { type, ...meta },
    };
  }

  // Resolves a data-flow endpoint to its export node, or undefined when the underlying system/entity no longer exists.
  private resolveFlowNode(node: AssessmentDataFlowNode): DataFlowNodeExport | undefined {
    if (node.nodeType === DataFlowNodeTypeEnum.System) {
      const system = this.systemsQuery.getEntity(node.entityId);
      return system
        ? this.buildFlowNode(system.name, DataFlowNodeTypeEnum.System, {
            serverLocations: this.resolveNodeLocations(system.serverLocations),
            iconPath: system.iconPath,
          })
        : undefined;
    }
    if (node.nodeType === DataFlowNodeTypeEnum.CustomEntity) {
      const custom = this.resolveCustomEntity(node.entityId);
      return custom
        ? this.buildFlowNode(custom.name, DataFlowNodeTypeEnum.CustomEntity, {
            serverLocations: this.resolveNodeLocations(custom.additionalData?.serverLocations),
          })
        : undefined;
    }
    return undefined;
  }

  private resolveEntityName(id: string): string {
    return this.customFieldsV2Service.getOption(id)?.text ?? id;
  }

  private resolveCustomEntity(id: string): { name: string; additionalData?: { serverLocations?: string[] } } | undefined {
    const option = this.customFieldsV2Service.getOption(id);
    return option ? { name: option.text, additionalData: option.additionalData } : undefined;
  }

  public async buildInstanceForExport(
    page: AssessmentPage,
    employees: EmployeeDataMapping[],
    ignoreIncompleteFields: boolean,
  ): Promise<AssessmentExportTemplateInstance> {
    const {
      name,
      sections,
      lastApprovedAt,
      type,
      createdDate,
      reviewDate,
      state,
      tags,
      layout,
      stateUpdatedAt,
      updatedDate,
    } = page;

    let templateType = type;
    let labels = [];

    this.loggerService.info(this.loggerName, `Building instance "${name}" for export...`);
    const labelsObject = await firstValueFrom(this.aiAssessmentLabelsPipe.transform(tags));
    labels = labelsObject?.labels;
    templateType = null;

    const normalizedLayout = this.assessmentsMapperService.normalizeLayoutObject(layout);
    const sectionOrdinality = new Map<AssessmentResourceId, number>(normalizedLayout?.sectionOrdinality);

    const controls = await this.buildSectionsForExport(
      page,
      sections,
      employees,
      ignoreIncompleteFields,
      sectionOrdinality,
    );

    const owner = employees.find(({ id }) => id === page.ownerId);
    const parsedInstance = {
      name,
      ownerName: owner ? `${owner.firstName} ${owner.lastName}` : this.contentPipe.transform('pdf-generator.empty'),
      approvedAt: lastApprovedAt ?? null,
      templateType: templateType ?? null,
      labels: labels ?? null,
      controls,
      createdDate,
      state: this.aiAssessmentStatusPipe.transform(page)?.status?.value ?? state,
      reviewDate,
      stateUpdatedAt: stateUpdatedAt ?? null,
      updatedDate: updatedDate ?? null,
    };

    this.loggerService.info(this.loggerName, `Finished building instance "${name}" built for export.`);
    return parsedInstance;
  }

  private async buildSectionsForExport(
    page: AssessmentPage,
    sections: AssessmentSection[],
    employees: EmployeeDataMapping[],
    ignoreIncompleteFields: boolean = false,
    sectionOrdinality?: Map<AssessmentResourceId, number>,
  ): Promise<AssessmentExportTemplateSection[]> {
    const sectionsPromises = sections.map(async section => {
      const sectionOrdinal = sectionOrdinality?.get(section.formControlId);
      const { name, label, layout } = section;
      const normalizedLayout = this.assessmentsMapperService.normalizeLayoutObject(layout);
      const controlsOrdinality = new Map<AssessmentResourceId, number>(normalizedLayout?.questionOrdinality);
      const controls = await this.iterateControls(
        page,
        section.questions,
        employees,
        ignoreIncompleteFields,
        controlsOrdinality,
      );

      return { 
        type: AssessmentExportedQuestionTypeEnum.Group, 
        name, 
        label, 
        controls: controls.sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)), 
        ordinal: sectionOrdinal ?? 0 
      };
    });

    const sectionsWithControls = await Promise.all(sectionsPromises);
    if (!ignoreIncompleteFields) {
      return sectionsWithControls;
    }

    return sectionsWithControls
      .filter(({ controls }) => controls.length > 0)
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));
  }

  private async iterateControls(
    page: AssessmentPage,
    questions: AssessmentQuestion[],
    employees: EmployeeDataMapping[],
    ignoreIncompleteFields: boolean,
    controlsOrdinality: Map<AssessmentResourceId, number>,
  ): Promise<AssessmentExportTemplateSection[]> {
    if (!questions || questions?.length === 0) {
      return [];
    }
    const sectionPromises = questions.map(async (control) => {
      this.loggerService.debug(this.loggerName, `Building section "${control.name}"...`);
      const section = await this.buildCustomSectionData(
        page,
        control,
        questions,
        employees,
      );

      const ordinal = controlsOrdinality.get(control.formControlId);
      this.loggerService.debug(this.loggerName, `Finished building custom section "${control.name}".`);
      // reasoning/reasoningSuggestedAt feed the "Include evidence" option (DOCX + PDF, server-side).
      // Resolve uploaded-document citations to signed download URLs so they render as working
      // clickable links in the exported doc.
      const reasoning = await this.resolveEvidenceReasoning(control.reasoning);
      return {
        ...section,
        ordinal: ordinal ?? 0,
        reasoning,
        reasoningSuggestedAt: control.reasoningSuggestedAt ?? null,
      };
    });
    const sections = await Promise.all(sectionPromises);
    if (!ignoreIncompleteFields) {
      return sections;
    }
    return sections.filter(section => !this.isSectionEmpty(section));
  }

  private async buildCustomSectionData(
    page: AssessmentPage,
    control: AssessmentQuestion,
    questions: AssessmentQuestion[],
    employees: EmployeeDataMapping[],
  ): Promise<AssessmentExportTemplateSection> {
    const { type, label } = control;

    if (type === AssessmentQuestionTypeEnum.Risks) {
      const riskData = this.buildRiskData(control);
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value: riskData };
    }
    if (type === AssessmentQuestionTypeEnum.DpiaNeeded) {
      const value = [{ value: this.contentPipe.transform(control.value ? 'ropa.dpiaEnable' : 'ropa.dpiaDisable') }];
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if (type === AssessmentQuestionTypeEnum.Text) {
      const value = control.value;
      return { type: AssessmentExportedQuestionTypeEnum.Text, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if (type === AssessmentQuestionTypeEnum.TextBlock) {
      const value = control.label;
      return { type: AssessmentExportedQuestionTypeEnum.TextBlock, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if (type === AssessmentQuestionTypeEnum.DataTypes) {
      const value = await this.mapDataTypes(questions);
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if (type === AssessmentQuestionTypeEnum.DataFlow) {
      const value = await this.buildDataFlowData(page, control);
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if (type === AssessmentQuestionTypeEnum.PartiesWithAccess) {
      const value = control.value as PartiesWithAccess;
      let listItems = [];
      if (value && value.type && value.description) {
        listItems = [{ value: value.type }, { value: value.description }];
      }
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value: listItems };
    }
    if ([AssessmentQuestionTypeEnum.LinkedAssessment, AssessmentQuestionTypeEnum.BusinessPurpose].includes(type)) {
      const listItems = this.collectRelatedAssessments(control);
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value: listItems };
    }
    if (type === AssessmentQuestionTypeEnum.File) {
      const fileValue = control.value;
      const attachments: AssessmentAttachment[] = Array.isArray(fileValue)
        ? fileValue as AssessmentAttachment[]
        : (fileValue && typeof fileValue === 'object' && 'id' in fileValue ? [fileValue as AssessmentAttachment] : []);
      const listItems = attachments
        .filter(attachment => !!attachment?.id)
        .map(attachment => ({
          value: attachment.filename,
        }));
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value: listItems };
    }
    if ([
      AssessmentQuestionTypeEnum.BusinessUnit, 
      AssessmentQuestionTypeEnum.DataSubjects, 
      AssessmentQuestionTypeEnum.LegalBasis, 
      AssessmentQuestionTypeEnum.TransferMechanism
    ].includes(control.type)) {
      const value = [];
      if (control.value) {
        const entities = control.value as AssessmentEntity[];
        for (let i = 0; i < entities.length; i++) {
          const entity = entities[i];
          value.push({ value: this.resolveEntityName(entity.id as string) });
        }
      }
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if (AssessmentQuestionTypeEnum.DataRole === control.type) {
      const value = [];
      if (control.value) {
        if (typeof control.value === 'string') {
          value.push({value: control.value});
        }
        else if (Array.isArray(control.value)) {
          for (let i = 0; i < control.value.length; i++) {
            const val = control.value[i];
            value.push({value: val});
          }
        }
      }
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if ([AssessmentQuestionTypeEnum.BusinessOwner, AssessmentQuestionTypeEnum.DataSources].includes(control.type)) {
      const value = [];
      if (control.value) {
        const ids = control.value as AssessmentResourceId[];

        for (let i = 0; i < ids.length; i++) {
          const id = ids[i];

          switch (control.type) {
            case AssessmentQuestionTypeEnum.BusinessOwner: {
              const employee = employees.find(employee => employee.id === id);
              if (!employee) {
                continue;
              }
              value.push({ value: `${employee.firstName} ${employee.lastName}` });
              break;
            }

            case AssessmentQuestionTypeEnum.DataSources: {
              const system = this.systemsQuery.getEntity(id);
              if (!system) {
                continue;
              }
              const additional =
                (Object.values(SpecialSystemType) as string[]).includes(system.systemType)
                  ? ''
                  : system.systemId;
              value.push({
                value: system.name,
                iconPath: system.iconPath,
                additional,
                serverLocations: system.serverLocations
              });
              break;
            }
          }
        }
      }
      
      return { type: AssessmentExportedQuestionTypeEnum.CustomComponent, name: this.exportQuestionTypeMap.get(type), label, value };
    }
    if (type === AssessmentQuestionTypeEnum.Dropdown) {
      const value = control.value as AssessmentDropdownOption[];
      const listItems = value?.filter(item => item.isSelected)?.map(item => item.label)?.join(', ') ?? '';
      return { type: AssessmentExportedQuestionTypeEnum.Dropdown, name: this.exportQuestionTypeMap.get(type), label, value: { value: listItems } };
    }
    if (type === AssessmentQuestionTypeEnum.DropdownCustomField) {
      const value = control.value as AssessmentCustomFieldDropdown;
      const listItems = value?.options?.filter(item => item.isSelected)?.map(item => item.label)?.join(', ') ?? '';
      return { type: AssessmentExportedQuestionTypeEnum.Dropdown, name: this.exportQuestionTypeMap.get(type), label, value: { value: listItems } };
    }
    if (type === AssessmentQuestionTypeEnum.TextCustomField) {
      const value = control.value as AssessmentCustomFieldText;
      return { type: AssessmentExportedQuestionTypeEnum.Text, name: this.exportQuestionTypeMap.get(type), label, value: value.textValue };
    }

    return { type: this.exportQuestionTypeMap.get(type), name: this.exportQuestionTypeMap.get(type), label, value: control.value };
  }

  private isSectionEmpty({ value }: AssessmentExportTemplateSection) {
    try {
      if (!value) return true;
      const isEmptyString = value === '' || value?.value === '';
      const isEmtpyArray = value instanceof Array && value.length === 0;
      const isEmptyObject = typeof value === 'object' && Object.keys(value).length === 0;
      const isEmptyDataFlow = value?.nodes && value.nodes.length === 0;
  
      return !value ||
        isEmptyString ||
        isEmtpyArray ||
        isEmptyObject ||
        isEmptyDataFlow;
    } 
    catch (error) {
      console.error('AiAssessmentsExportHelper.isSectionEmpty error', { error, value });
      return true;
    }
  }

  private buildRiskData(control: AssessmentQuestion) {
    const result = {};
    const risks = control.value as Risk[];
    for (let i = 0; i < risks.length; i++) {
      const curr = risks[i];
      result[curr.type] = {
        ...curr,
        likelihood: { id: curr.likelihood, value: this.riskCalculationTextPipe.transform(curr.likelihood) },
        severity: { id: curr.severity, value: this.riskCalculationTextPipe.transform(curr.severity) },
        calculatedRisk: { id: curr.inherentRisk, value: this.riskCalculationTextPipe.transform(curr.inherentRisk) },
        residualRisk: { id: curr.residualRisk, value: this.riskCalculationTextPipe.transform(curr.residualRisk) }
      };
    }
    return result;
  }

  private async mapDataTypes(
    questions: AssessmentQuestion[],
  ): Promise<AssessmentSectionListItem[]> {
    if (!questions || !questions?.length) {
      return [];
    }
    const systemEntities = questions?.filter(({ type }) => type === AssessmentQuestionTypeEnum.DataSources)?.map(({ value }) => value as AssessmentResourceId[])?.flat();
    const dataTypes = questions?.filter(({ type }) => type === AssessmentQuestionTypeEnum.DataTypes)?.map(({ value }) => value as AssessmentDataTypeSource[])?.flat();
    const allSourcesPlaceholder = this.contentPipe.transform('ai-assessments.dataTypes.allSourcesPlaceholder');

    const customValues$ = this.customValuesQuery.selectCustomValuesGroupedByType().pipe(
      map(customValuesAll => customValuesAll.get(CustomValueTypeEnum.DataType))
    );
    const customValues = await firstValueFrom(customValues$);

    return dataTypes.map(dataType => {
      const dataTypeSystems = dataType.dataSources;
      
      // Filter dataTypeSystems to only include systems that exist in the current assessment's dataSources
      const filteredDataTypeSystems = dataTypeSystems?.filter(id => systemEntities?.includes(id)) ?? [];

      let joinedSystemNames = '';
      if (filteredDataTypeSystems?.length === systemEntities?.length) {
        joinedSystemNames = allSourcesPlaceholder;
      } else {
        joinedSystemNames = filteredDataTypeSystems?.map(id => this.systemsQuery.getEntity(id)?.name ?? id)?.filter(Boolean)?.join(', ');
      }

      return {
        value: this.resolveEntityName(dataType.dataType.id as string),
        additional: joinedSystemNames
      };
    });
  }

  private async buildDataFlowData(page: AssessmentPage, control: AssessmentQuestion): Promise<DataFlowDataExport> {
    const value = control.value as AssessmentDataFlow;
    const nodes: DataFlowNodeExport[] = [];
    const edges: DataFlowEdgeExport[] = [];

    for (const edge of value?.edges ?? []) {
      const sourceNode = this.resolveFlowNode(edge.source);
      const targetNode = this.resolveFlowNode(edge.target);

      // Resolve both endpoints before pushing either node, so a missing endpoint never leaves an orphan in the payload.
      if (!sourceNode || !targetNode) {
        continue;
      }

      if (!nodes.find(node => node.id === sourceNode.id)) {
        nodes.push(sourceNode);
      }
      if (!nodes.find(node => node.id === targetNode.id)) {
        nodes.push(targetNode);
      }

      if (sourceNode.id !== targetNode.id) {
        edges.push({
          id: CSS.escape(`${sourceNode.id}-${targetNode.id}`),
          source: sourceNode.id,
          target: targetNode.id,
        });
      }
    }

    return {
      nodes,
      edges,
    };
  }

  collectRelatedAssessments(control: AssessmentQuestion): AssessmentSectionListItem[] {
    const listItems: AssessmentSectionListItem[] = [];
    (control.value as AssessmentPage[])?.map(page => {
      const assessment = page;
      if (!assessment) {
        return;
      }

      const status = this.aiAssessmentStatusPipe.transform(assessment);
      const cmsTypes = this.contentPipe.transform('ai-assessments.types');
      const emptyValue = this.contentPipe.transform('pdf-generator.empty');
      const templateType = cmsTypes[AssessmentTemplateEnum[assessment.type]];

      return listItems.push({
        value: `${assessment.name} (${templateType})`,
        additional: `${status?.status?.value ?? emptyValue} - ${status.date}`,
      });
    });
    return listItems;
  }

  public imageToBase64(url: string): Promise<string> {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = () => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result.toString());
        };
        reader.onerror = () => {
          this.loggerService.error(this.loggerName, `Error converting image to base64: ${url}.`);
          resolve('');
        };
        reader.readAsDataURL(xhr.response);
      };
      // Without this, a network/CORS/DNS failure never settles the promise, hanging the whole export
      // payload build. Resolve '' so the url is simply dropped from the images map (the server-side
      // Base64ImagePipe then renders a local fallback instead of re-fetching the external icon).
      xhr.onerror = () => {
        this.loggerService.error(this.loggerName, `Error fetching image for base64 conversion: ${url}.`);
        resolve('');
      };
      // onerror misses a TCP-level stall (connection open, no data, no RST); a timeout closes that gap
      // so a hung icon host can never block the export payload build indefinitely.
      xhr.ontimeout = () => {
        this.loggerService.error(this.loggerName, `Timeout fetching image for base64 conversion: ${url}.`);
        resolve('');
      };
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.timeout = 10000; // 10s — icons are small; anything longer is a stall
      xhr.send();
    });
  }

  async imagesToBase64ByUrl(urls: string[]): Promise<Record<string, string>> {
    const imagePromises = urls.map(url => this.imageToBase64(url));
    const imagesInBase64 = await Promise.all(imagePromises);
    const imagesByUrl: Record<string, string> = {};
    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      imagesByUrl[url] = imagesInBase64[i];
    }
    return imagesByUrl;
  }

  extractImagesInBase64(instances: AssessmentExportTemplateInstance[]): Promise<Record<string, string>> {
    const imageUrls: Set<string> = new Set();
    for (let i = 0; i < instances.length; i++) {
      const instance = instances[i];
      for (let j = 0; j < instance.controls.length; j++) {
        const { controls } = instance.controls[j];
        for (let k = 0; k < controls.length; k++) {
          const { value } = controls[k];
          if (value instanceof Array) {
            const filtered = value.filter(item => item && item.iconPath).map(item => item.iconPath);
            for (let l = 0; l < filtered.length; l++) {
              const url = filtered[l];
              imageUrls.add(url);
            }
          }
          if (value?.nodes) {
            const filtered = value.nodes.filter(node => node.meta && node.meta.iconPath).map(node => node.meta.iconPath);
            for (let l = 0; l < filtered.length; l++) {
              const url = filtered[l];
              imageUrls.add(url);
            }
          }
        }
      }
    }
    return this.imagesToBase64ByUrl(Array.from(imageUrls));
  }

  async getBusinessOwnerData(pages: AssessmentPage[], additionalEmployeeIds: string[] = []): Promise<EmployeeDataMapping[]> {
    const employeeIds = pages.reduce((allIds, { ownerId }) => {
      if (ownerId) {
        allIds.add(ownerId);
      }

      const businessOwnerIds = pages.flatMap(({ sections }) => 
        sections.flatMap(({ questions }) => 
          questions.filter(({ type }) => 
            type === AssessmentQuestionTypeEnum.BusinessOwner).flatMap(({ value }) => value as AssessmentResourceId[])));

      for (let i = 0; i < businessOwnerIds.length; i++) {
        const id = businessOwnerIds[i];
        if (id) {
          allIds.add(''+id);
        }
      }
      return allIds;
    }, new Set<string>(additionalEmployeeIds));
    const uniqueEmployeeIds = Array.from(employeeIds).filter(Boolean);
    if (uniqueEmployeeIds.length === 0) {
      return [];
    }
    return firstValueFrom(this.employeesService.getEmployeesByIds(uniqueEmployeeIds));
  }
}